import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MarkdownText } from './MarkdownText';

export interface Reaction {
  readonly emoji: string;
  readonly count: number;
}

export interface Comment {
  readonly commentId: string;
  readonly authorId?: string;
  readonly authorName: string;
  readonly authorAvatar: string;
  readonly text: string;
  readonly timestamp: string;
  readonly editedAt?: string;
  readonly reactions: Reaction[];
}

export interface MentionableUser {
  readonly userId: string;
  readonly displayName: string;
}

export interface CommentsProps {
  readonly comments: Comment[];
  readonly currentUserId: string;
  readonly onAddComment: (text: string) => void;
  readonly onDeleteComment: (commentId: string) => void;
  readonly onAddReaction: (commentId: string, emoji: string) => void;
  readonly onEditComment?: (commentId: string, newText: string) => void;
  readonly mentionableUsers?: MentionableUser[];
}

/**
 * Formats a timestamp into a human-readable relative format.
 */
function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diff = now - then;

  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  return new Date(timestamp).toLocaleDateString();
}

/**
 * Comments section — displays comments in reverse chronological order (newest first)
 * with author info, relative timestamp, reactions, and a text area to add new comments.
 *
 * Requirements: 14.1-14.5
 */
export function Comments({
  comments,
  currentUserId,
  onAddComment,
  onDeleteComment,
  onAddReaction,
  onEditComment,
  mentionableUsers,
}: CommentsProps): React.ReactElement {
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  // Mention autocomplete state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionTriggerPos, setMentionTriggerPos] = useState(-1);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);

  // Filter mentionable users by the current query
  const mentionResults = useCallback((): MentionableUser[] => {
    if (!mentionableUsers || mentionQuery === null) return [];
    if (mentionQuery === '') return mentionableUsers.slice(0, 8);
    const q = mentionQuery.toLowerCase();
    return mentionableUsers.filter((u) => u.displayName.toLowerCase().includes(q)).slice(0, 8);
  }, [mentionableUsers, mentionQuery]);

  const filteredMentions = mentionResults();

  // Detect @ trigger in textarea input
  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewComment(value);

    if (!mentionableUsers || mentionableUsers.length === 0) return;

    const cursorPos = e.target.selectionStart ?? value.length;
    const textBeforeCursor = value.slice(0, cursorPos);

    // Find the last @ that's preceded by whitespace or start of text
    let atPos = -1;
    for (let i = textBeforeCursor.length - 1; i >= 0; i--) {
      if (textBeforeCursor[i] === '@') {
        if (i === 0 || /[\s\n]/.test(textBeforeCursor[i - 1])) {
          atPos = i;
          break;
        }
      }
      // Stop searching if we hit a space (no active mention trigger)
      if (textBeforeCursor[i] === ' ' && i < textBeforeCursor.length - 1) {
        // Only stop if we haven't found @ yet and there's a space break
        // Actually, allow spaces within search (for multi-word names)
      }
      if (textBeforeCursor[i] === '\n') break;
    }

    if (atPos >= 0) {
      const query = textBeforeCursor.slice(atPos + 1);
      // Close if query is too long or has newlines
      if (query.length > 30 || query.includes('\n')) {
        setMentionQuery(null);
        setMentionTriggerPos(-1);
      } else {
        setMentionQuery(query);
        setMentionTriggerPos(atPos);
        setMentionIndex(0);
      }
    } else {
      setMentionQuery(null);
      setMentionTriggerPos(-1);
    }
  }, [mentionableUsers]);

  // Insert selected mention into textarea
  const insertMention = useCallback((user: MentionableUser) => {
    if (mentionTriggerPos < 0) return;
    const before = newComment.slice(0, mentionTriggerPos);
    const cursorPos = textareaRef.current?.selectionStart ?? newComment.length;
    const after = newComment.slice(cursorPos);
    const inserted = `@${user.displayName} `;
    setNewComment(before + inserted + after);
    setMentionQuery(null);
    setMentionTriggerPos(-1);

    // Refocus textarea and set cursor after the mention
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + inserted.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  }, [mentionTriggerPos, newComment]);

  // Keyboard handling for mention dropdown
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery === null || filteredMentions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex((prev) => Math.min(prev + 1, filteredMentions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (filteredMentions[mentionIndex]) {
        e.preventDefault();
        insertMention(filteredMentions[mentionIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMentionQuery(null);
      setMentionTriggerPos(-1);
    }
  }, [mentionQuery, filteredMentions, mentionIndex, insertMention]);

  const handleSubmit = () => {
    const trimmed = newComment.trim();
    if (trimmed.length === 0) return;
    onAddComment(trimmed);
    setNewComment('');
  };

  const handleStartEdit = (comment: Comment) => {
    setEditingCommentId(comment.commentId);
    setEditText(comment.text);
  };

  const handleSaveEdit = () => {
    if (!editingCommentId) return;
    const trimmed = editText.trim();
    if (trimmed.length === 0) return;
    onEditComment?.(editingCommentId, trimmed);
    setEditingCommentId(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditText('');
  };

  return (
    <section aria-label="Comments" className="space-y-4">
      {/* Input form with textarea and mention autocomplete */}
      <div className="space-y-2 relative">
        <label htmlFor="comment-input" className="sr-only">Add a comment</label>
        <textarea
          ref={textareaRef}
          id="comment-input"
          value={newComment}
          onChange={handleCommentChange}
          onKeyDown={handleKeyDown}
          placeholder="Write a comment... (type @ to mention someone)"
          aria-label="Add a comment"
          rows={3}
          className="w-full border border-border rounded-md px-3 py-2 text-sm bg-card text-card-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
        />

        {/* Mention autocomplete dropdown */}
        {mentionQuery !== null && filteredMentions.length > 0 && (
          <div
            ref={mentionDropdownRef}
            className="absolute bottom-full mb-1 left-0 w-64 max-h-48 overflow-y-auto rounded-md border bg-popover shadow-lg z-50"
            role="listbox"
            aria-label="Mention suggestions"
          >
            {filteredMentions.map((user, index) => (
              <button
                key={user.userId}
                type="button"
                role="option"
                aria-selected={index === mentionIndex}
                className={`w-full text-left px-3 py-2 text-sm cursor-pointer transition-colors
                  ${index === mentionIndex ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}
                `}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent textarea blur
                  insertMention(user);
                }}
                onMouseEnter={() => setMentionIndex(index)}
              >
                <span className="truncate block">@{user.displayName}</span>
              </button>
            ))}
          </div>
        )}

        <button
          onClick={handleSubmit}
          aria-label="Add comment"
          className="bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Add comment
        </button>
      </div>

      {/* Comment list (newest first) */}
      <div className="space-y-3">
        {comments.map((comment) => (
          <article key={comment.commentId} className="flex gap-3">
            {comment.authorAvatar ? (
              <img src={comment.authorAvatar} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/20 text-xs font-semibold text-primary">
                {comment.authorName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-card-foreground">{comment.authorName}</span>
                <time dateTime={comment.timestamp} className="text-xs text-muted-foreground" role="time">
                  {formatRelativeTime(comment.timestamp)}
                </time>
                {comment.editedAt && (
                  <span className="text-xs text-muted-foreground">(edited)</span>
                )}
              </div>
              {editingCommentId === comment.commentId ? (
                <div className="mt-1 space-y-2">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    aria-label="Edit comment"
                    rows={3}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-card text-card-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveEdit}
                      className="bg-primary text-primary-foreground px-3 py-1 rounded-md text-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="bg-muted text-muted-foreground px-3 py-1 rounded-md text-sm hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-sm text-card-foreground mt-1">
                    <MarkdownText text={comment.text} />
                  </div>
                  {comment.authorId === currentUserId && onEditComment && (
                    <button
                      onClick={() => handleStartEdit(comment)}
                      className="text-xs text-muted-foreground hover:text-card-foreground mt-1 focus:outline-none focus:ring-2 focus:ring-ring rounded"
                      aria-label={`Edit comment by ${comment.authorName}`}
                    >
                      Edit
                    </button>
                  )}
                </>
              )}
              {comment.reactions.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {comment.reactions.map((r) => (
                    <button
                      key={r.emoji}
                      onClick={() => onAddReaction(comment.commentId, r.emoji)}
                      className="text-xs bg-muted px-2 py-0.5 rounded hover:bg-muted/80"
                    >
                      {r.emoji} {r.count}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
